// INV-001: the 01 Experience invoice (chauffeur and tour bookings only).
// Pricing, line items and the printable document live here so the numbers can
// be checked on their own. 01 Motors invoices (backend/invoices.py,
// InvoiceManager, InvoiceEditor) are a separate system and never use this file.

export const TAX_RATE = 0.13;

export const COMPANY = {
  name: "01 EXPERIENCE",
  address: "1652 Bayly St, Pickering, ON L1W 1L9",
  phone: "(647) 768-0088",
  email: "01ExperienceGTA@gmail.com",
};

const num = (v) => Number(v || 0);
const r2 = (x) => Math.round(x * 100) / 100;
const rows = (v) => (Array.isArray(v) ? v : []);

export const money = (n) =>
  `$${num(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (d = new Date()) =>
  `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;

export const fmtTime = (s) =>
  s ? new Date(s).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";

export const fmtService = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : `${fmtDate(d)} · ${fmtTime(s)}`;
};

// One row per charge the booking carries, built from the same fields the
// booking form adds up into Total (bookingFields.computeChargesTotal):
// hours x rate (or the tour package), standby, custom add-ons, delivery and
// other charges. A whole-job (flat) booking has no rows: its Total is the price.
export function invoiceLines(b) {
  const tour = b.booking_type === "tour";
  const hourly = (b.pricing_mode || "hourly") !== "flat";
  const lines = [];
  if (!hourly) return lines;
  if (tour && String(b.package_id || "").trim()) {
    lines.push({ kind: "main", label: b.package_name || "Tour package", amount: r2(num(b.package_base)) });
    for (const s of rows(b.package_selections)) {
      if (num(s?.price)) lines.push({ kind: "item", label: [s.item_label, s.option_label].filter(Boolean).join(": ") || "Package option", amount: r2(num(s.price)) });
    }
    for (const a of rows(b.package_addons)) {
      if (num(a?.price)) lines.push({ kind: "item", label: a.label || "Package add-on", amount: r2(num(a.price)) });
    }
  } else if (num(b.service_hours) > 0 && num(b.hourly_rate) > 0) {
    const hours = num(b.service_hours), rate = num(b.hourly_rate);
    lines.push({ kind: "main", hours, rate, amount: r2(hours * rate) });
  }
  if (!tour && num(b.standby_hours) > 0 && num(b.standby_rate) > 0) {
    const hours = num(b.standby_hours), rate = num(b.standby_rate);
    lines.push({ kind: "item", label: "Standby time", hours, rate, amount: r2(hours * rate) });
  }
  for (const a of rows(b.custom_addons)) {
    if (num(a?.price)) lines.push({ kind: "item", label: a.label || "Add-on", amount: r2(num(a.price)) });
  }
  if (num(b.delivery)) lines.push({ kind: "item", label: "Delivery", amount: r2(num(b.delivery)) });
  if (num(b.other_charges)) lines.push({ kind: "item", label: "Other charges", amount: r2(num(b.other_charges)) });
  return lines;
}

// The subtotal is the booking's own Total (before HST), the same number the
// booking page and the payment link use. The rows are printed only when they
// add up to it exactly; otherwise (whole-job price, or a Total typed by hand)
// the invoice shows one line for the Total, so it can never disagree with itself.
export function computePricing(b, inv = {}) {
  const subtotal = r2(num(b.total || b.amount));
  const items = invoiceLines(b);
  const sum = r2(items.reduce((s, l) => s + l.amount, 0));
  const itemised = items.length > 0 && Math.abs(sum - subtotal) < 0.005;
  const tax = b.tax_applied ? r2(subtotal * TAX_RATE) : 0;
  const total = r2(subtotal + tax);
  const deposit = r2(num(b.depo_sent));
  const paid = inv.paid ? total : Math.min(deposit, total);
  return { subtotal, tax, total, deposit, paid, balance: r2(total - paid), lines: itemised ? items : null };
}

// HST registration numbers are 9 digits + RT + 4 digits, e.g. 123456789 RT0001.
export const normalizeHst = (v) => {
  const s = String(v || "").replace(/\s+/g, "").toUpperCase();
  if (!s) return "";
  return /^\d{9}RT\d{4}$/.test(s) ? `${s.slice(0, 9)} ${s.slice(9)}` : null;
};

// The public logo is a black square on a white sheet. The band shows only the
// square: these are its pixel bounds inside the 3200 x 2134 file.
const LOGO = { w: 3200, x: 550, y: 192, cw: 2100, ch: 1749 };
const logoHtml = (src, hPx) => {
  const s = hPx / LOGO.ch;
  const f = (v) => (v * s).toFixed(2);
  return `<div class="logo" style="width:${f(LOGO.cw)}px;height:${hPx}px"><img src="${src}" alt="01 Experience" style="width:${f(LOGO.w)}px;margin-left:-${f(LOGO.x)}px;margin-top:-${f(LOGO.y)}px" /></div>`;
};

export function documentHtml(b, inv, p, { origin = "", settings = {} } = {}) {
  const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const br = (v) => esc(v).replace(/\n/g, "<br>");
  const logo = logoHtml(`${origin}/01-experience-logo.png`, 72);
  const hst = String(settings.hst_number || "").trim();
  const howToPay = String(settings.payment_instructions || "").trim();
  const billName = String(inv.bill_to_name || "").trim();
  const billEmail = String(inv.bill_to_email || "").trim();
  const toGuest = !billName;
  const title = inv.service_title || b.tour_name || "Private chauffeur service";
  const driver = String(b.driver_name || "").trim();
  const detail = [b.vehicle_label, driver && `chauffeur ${driver}`, b.start_location && `pickup ${b.start_location}`].filter(Boolean).join(" · ");
  const cell = (v) => (v === undefined || v === null || v === "" ? "" : v);

  const mainRow = (amount, hours, rate, label) => `
      <tr><td><span class="t">${esc(label && label !== title ? `${title} (${label})` : title)}</span>${detail ? `<small>${esc(detail)}</small>` : ""}${inv.line_description ? `<small class="para">${esc(inv.line_description)}</small>` : ""}</td>
        <td class="n">${cell(hours)}</td><td class="n">${rate ? money(rate) : ""}</td><td class="n">${money(amount)}</td></tr>`;
  const body = p.lines
    ? p.lines.map((l) => l.kind === "main"
        ? mainRow(l.amount, l.hours, l.rate, l.label)
        : `
      <tr><td>${esc(l.label)}</td><td class="n">${cell(l.hours)}</td><td class="n">${l.rate ? money(l.rate) : ""}</td><td class="n">${money(l.amount)}</td></tr>`).join("")
    : mainRow(p.subtotal);

  const inclusions = (inv.inclusions || []).map((x) => `<li>${esc(x)}</li>`).join("");
  const steps = (inv.itinerary_steps || []).map((s, i) => `
    <div class="step"><div class="num">${i + 1}</div><div><div class="st">${esc(s.title)}</div><div class="sd">${esc(s.description)}</div></div></div>`).join("");
  const due = p.balance;
  const pickup = [fmtTime(b.start_datetime), b.start_location].filter(Boolean).join(" · ");
  const facts = [["Guest", b.customer_name], ["Pickup", pickup], ["Vehicle", b.vehicle_label], ["Chauffeur", driver]].filter(([, v]) => v);
  const foot = (left) => `<div class="foot"><span>${left}</span><b>01 EXPERIENCE</b></div>`;
  const band = (label) => `<div class="band">${logo}<div class="doc"><span class="lbl">${label}</span><span class="no">${esc(inv.invoice_number)}</span></div></div>`;

  const itinerary = inv.include_itinerary === false ? "" : `
<div class="page">
  ${band("Itinerary")}
  <div class="body">
    <div class="meta" style="grid-template-columns:repeat(${Math.max(facts.length, 1)},minmax(0,1fr))">
      ${facts.map(([l, v]) => `<div><span class="lbl">${l}</span>${esc(v)}</div>`).join("")}
    </div>
    <h1>${esc(inv.itinerary_subtitle || title)}</h1>
    <div class="steps">${steps || `<p class="muted">No itinerary steps added.</p>`}</div>
    ${inv.operational_note ? `<div class="note"><span class="lbl">Operational note</span><p>${br(inv.operational_note)}</p></div>` : ""}
  </div>
  ${foot("For guest reference and operational coordination.")}
</div>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.invoice_number)} · ${esc(billName || b.customer_name || "Guest")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" /><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,100..900&display=swap" rel="stylesheet" />
<style>
  @page { size: letter; margin: 0; }
  :root { --ink:#0E0F11; --muted:#5B6068; --line:#DADDE1; --silver:#A9AFB7; --white:#F3F4F6; --soft:#F5F6F7; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; background: #fff; color: var(--ink); font-family: "Archivo", "Helvetica Neue", Arial, sans-serif; }
  .page { width: 8.5in; min-height: 10.98in; display: flex; flex-direction: column; background: #fff; break-after: page; }
  .page:last-child { break-after: auto; }
  .band { background: #050505; color: var(--white); padding: 18px 48px; display: flex; justify-content: space-between; align-items: center; }
  .logo { overflow: hidden; flex: none; }
  .logo img { display: block; }
  .doc { text-align: right; display: flex; flex-direction: column; gap: 2px; }
  .lbl { font-size: 10px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--muted); }
  .band .lbl { color: var(--silver); }
  .no { font-stretch: 125%; font-weight: 650; font-size: 22px; letter-spacing: -.01em; }
  .body { padding: 28px 48px; display: flex; flex-direction: column; gap: 20px; flex: 1; }
  .from { display: flex; justify-content: space-between; gap: 24px; font-size: 12px; color: var(--muted); line-height: 1.6; }
  .from b { color: var(--ink); font-stretch: 125%; font-weight: 650; font-size: 13px; letter-spacing: .02em; }
  .from .to { text-align: right; }
  .from .to b { font-size: 14px; }
  .hst { color: var(--ink); font-weight: 600; }
  .meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
  .meta > div { padding: 12px 0; display: flex; flex-direction: column; gap: 4px; font-size: 13px; line-height: 1.5; }
  .meta > div + div { padding-left: 20px; border-left: 1px solid var(--line); }
  .meta .big { font-stretch: 125%; font-weight: 650; font-size: 16px; }
  .tag { display: inline-block; margin-left: 8px; padding: 1px 6px; border: 1px solid var(--ink); font-size: 10px; font-weight: 650; letter-spacing: .16em; vertical-align: 2px; }
  h1 { margin: 0; font-stretch: 125%; font-weight: 650; font-size: 26px; letter-spacing: -.01em; line-height: 1.1; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; padding: 0 0 10px; border-bottom: 1px solid var(--ink); }
  td { padding: 10px 0; border-bottom: 1px solid var(--line); vertical-align: top; }
  th.n, td.n { text-align: right; white-space: nowrap; padding-left: 16px; font-variant-numeric: tabular-nums; }
  td .t { font-weight: 600; }
  td small { display: block; color: var(--muted); font-size: 12px; margin-top: 3px; line-height: 1.5; }
  td small.para { margin-top: 6px; }
  .after { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; }
  .pay { flex: 1; max-width: 360px; background: var(--soft); padding: 14px 16px; font-size: 12px; line-height: 1.6; }
  .pay p { margin: 6px 0 0; }
  .sum { margin-left: auto; width: 280px; flex: none; font-size: 13px; display: flex; flex-direction: column; }
  .sum div { display: flex; justify-content: space-between; padding: 7px 0; font-variant-numeric: tabular-nums; }
  .sum span:last-child { white-space: nowrap; }
  .sum .tot { border-top: 1px solid var(--line); }
  .sum .due { border-top: 2px solid var(--ink); margin-top: 4px; padding-top: 12px; font-stretch: 125%; font-weight: 650; font-size: 17px; }
  ul.incl { list-style: none; padding: 0; margin: 8px 0 0; column-count: 2; column-gap: 32px; }
  ul.incl li { font-size: 12px; color: var(--muted); padding: 4px 0 4px 14px; position: relative; break-inside: avoid; }
  ul.incl li::before { content: ""; position: absolute; left: 0; top: 10px; width: 5px; height: 5px; background: var(--silver); }
  .steps { display: flex; flex-direction: column; }
  .step { display: flex; gap: 16px; padding: 14px 0; border-bottom: 1px solid var(--line); break-inside: avoid; }
  .num { flex: 0 0 28px; height: 28px; border: 1px solid var(--ink); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 650; }
  .st { font-size: 14px; font-weight: 600; }
  .sd { font-size: 12px; color: var(--muted); margin-top: 4px; line-height: 1.6; }
  .note { background: var(--soft); border-left: 2px solid var(--ink); padding: 14px 16px; font-size: 12px; line-height: 1.6; }
  .note p { margin: 6px 0 0; color: var(--muted); }
  .muted { color: var(--muted); font-size: 12px; }
  .foot { padding: 18px 48px; border-top: 1px solid var(--line); display: flex; justify-content: space-between; gap: 16px; font-size: 11px; color: var(--muted); }
  .foot b { letter-spacing: .16em; font-weight: 600; color: var(--ink); white-space: nowrap; }
</style></head><body>

<div class="page">
  ${band("Invoice")}
  <div class="body">
    <div class="from">
      <div><b>${esc(COMPANY.name)}</b><br>${esc(COMPANY.address)}<br>${esc(COMPANY.phone)} · ${esc(COMPANY.email)}${hst ? `<br><span class="hst">HST # ${esc(hst)}</span>` : ""}</div>
      <div class="to"><span class="lbl">Bill to</span><br><b>${esc(billName || b.customer_name || "Guest")}</b>${
        toGuest
          ? `${b.customer_email ? `<br>${esc(b.customer_email)}` : ""}${b.customer_phone ? `<br>${esc(b.customer_phone)}` : ""}`
          : `${billEmail ? `<br>${esc(billEmail)}` : ""}${b.customer_name ? `<br>Guest: ${esc(b.customer_name)}` : ""}`
      }</div>
    </div>
    <div class="meta">
      <div><span class="lbl">Issue date</span>${esc(inv.issue_date)}</div>
      <div><span class="lbl">Service date</span>${esc(fmtService(b.start_datetime) || "")}</div>
      <div><span class="lbl">Balance due</span><span class="big">${money(due)}${inv.paid ? `<span class="tag">PAID</span>` : ""}</span></div>
    </div>
    <h1>${esc(title)}</h1>
    <table>
      <thead><tr><th class="lbl">Description</th><th class="lbl n">Hours</th><th class="lbl n">Rate</th><th class="lbl n">Amount</th></tr></thead>
      <tbody>${body}
      </tbody>
    </table>
    <div class="after">
      ${howToPay ? `<div class="pay"><span class="lbl">How to pay</span><p>${br(howToPay)}</p></div>` : "<div></div>"}
      <div class="sum">
        <div><span>Subtotal</span><span>${money(p.subtotal)}</span></div>
        ${b.tax_applied ? `<div><span>HST 13%</span><span>${money(p.tax)}</span></div>` : ""}
        <div class="tot"><span>Total</span><span>${money(p.total)}</span></div>
        <div><span>Paid</span><span>${money(p.paid)}</span></div>
        <div class="due"><span>Balance due</span><span>${money(due)}</span></div>
      </div>
    </div>
    ${inclusions ? `<div><span class="lbl">Included</span><ul class="incl">${inclusions}</ul></div>` : ""}
  </div>
  ${foot(`Thank you for choosing 01 Experience. Questions: ${esc(COMPANY.phone)}.`)}
</div>
${itinerary}
</body></html>`;
}
